/* eslint-disable import/no-deprecated */
import { classNames } from '@fullstory/util/fmt';
import moment, { Moment } from 'moment';
import React, { ReactNode, useLayoutEffect, useState } from 'react';
import { Typography } from '../../../deprecated/Typography';
import { identifyComponent } from '../../../util';
import { Box } from '../../Box';
import { Flex } from '../../Flex';
import { usePreviewRange } from '../hooks';
import { Minimap } from '../Minimap';
import { Focus, FocusPosition, MomentRanges } from '../types';
import css from './index.module.scss';
import { MonthScroller } from './MonthScroller';

export interface CalendarProps {
  /**
   * The selected/comparison date ranges. The `Day` will be rendered differently
   * based on if/where it falls within the provided ranges.
   */
  ranges: MomentRanges;
  /**
   * Tuple including the `RangeIndex` of the range that is currently focused and the
   * current `FocusPosition` (start/end).
   */
  focus: Focus;
  /**
   * Handler that's called when user clicks a day.
   */
  onDayClick: (day: Moment) => void;
  /**
   * The minimum selectable date. If provided, days before this date will be disabled.
   */
  minDate?: Moment;
  /**
   * The maximum selectable date. If provided, days after this date will be disabled.
   */
  maxDate?: Moment;
  /**
   * Optional function that controls whether a given day is disabled. Useful for
   * limiting selection to a minimum of 3 days, for example.
   */
  isDayDisabled?: (day: Moment) => boolean;
  /**
   * Optional function to render an info tooltip for a given day. Useful for visualizing
   * an orgs retention period, for example.
   */
  renderInfoTooltip?: (day: Moment) => ReactNode;
}

const defaultMinDate = moment().subtract(18, 'months');
const defaultMaxDate = moment();
const daysOfWeek = moment.localeData().weekdaysMin();

export const Calendar: React.FC<CalendarProps> = ({
  ranges,
  focus,
  minDate = defaultMinDate,
  maxDate = defaultMaxDate,
  isDayDisabled,
  renderInfoTooltip,
  onDayClick,
  ...rest
}) => {
  const [focusedRange, focusPosition] = focus;
  const [viewportEl, setViewportEl] = useState<HTMLDivElement | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [hoveredDay, setHoveredDay] = useState<Moment | null>(null);
  const previewRange = usePreviewRange(hoveredDay, focusPosition, ranges[focusedRange]);

  useLayoutEffect(() => {
    if (viewportEl) {
      setScrollbarWidth(viewportEl.offsetWidth - viewportEl.scrollWidth);
    }
  }, [viewportEl]);

  return (
    <Flex
      data-component="Calendar"
      className={classNames(css.wrapper, css.flex)}
      alignItems="flex-start"
      {...identifyComponent('calendar')}
    >
      <Box className={css.calendar} height={1}>
        <Box
          padding={[8, 16]}
          className={classNames(css.daysOfWeek, css.overlay)}
          style={{
            paddingRight: 16 + scrollbarWidth,
          }}
        >
          {daysOfWeek.map(dayOfWeek => (
            <Typography key={dayOfWeek} textAlign="center" variant="heading7" color="neutrals-n7">
              {dayOfWeek}
            </Typography>
          ))}
        </Box>
        <Box ref={setViewportEl} className={css.content} overflow="auto">
          <MonthScroller
            ranges={ranges}
            focusedRange={focusedRange}
            onDayHover={setHoveredDay}
            preview={previewRange}
            viewport={viewportEl}
            minDate={minDate}
            maxDate={maxDate}
            isDayDisabled={isDayDisabled}
            renderInfoTooltip={renderInfoTooltip}
            onDayClick={day => {
              // merge time from date with date from input
              const dayCopy = moment(day);
              const currentInput =
                ranges[focusedRange][focusPosition <= FocusPosition.StartTime ? 'start' : 'end'];
              if (currentInput) {
                dayCopy.set({
                  hour: currentInput.get('hour'),
                  minute: currentInput.get('minute'),
                  second: currentInput.get('second'),
                  millisecond: currentInput.get('millisecond'),
                });
              }
              onDayClick(dayCopy);
            }}
            {...rest}
          />
        </Box>
      </Box>
      <Flex className={css.flex} alignItems="stretch" column>
        <Box className={css.overlay} flexShrink={0} />
        <Box className={css.content}>
          <Minimap
            ranges={ranges}
            focusedRange={focusedRange}
            viewport={viewportEl}
            onScrollChange={scrollY => {
              viewportEl?.scrollTo({
                top: scrollY,
              });
            }}
            minDate={minDate}
            maxDate={maxDate}
            isDayDisabled={isDayDisabled}
            renderInfoTooltip={renderInfoTooltip}
          />
        </Box>
      </Flex>
    </Flex>
  );
};
